import Stapes from 'stapes'; // Adjust this path accordingly

// Defaults and Utility Functions
const defaults = {
    headers: {},
    baseUrl: null,
    interceptors: []
};

const utils = {
    extend: (base, ...exts) => {
        exts.forEach(ext => {
            for (const key in ext) {
                if (ext.hasOwnProperty(key)) {
                    base[key] = ext[key];
                }
            }
        });
        return base;
    },
    flow: (data, callbacks) => {
        return callbacks.reduce((data, callback) => {
            if (Array.isArray(data)) {
                return data.map(callback);
            }
            return callback(data);
        }, data);
    }
};

const defaultOptions = { applyGlobalMiddlewares: true };
const globalMiddlewares = [];

// ModelManager Definition
const ModelManager = Stapes.subclass({
    constructor: function(model) {
        this.model = model;
    },

    get: function(pk, extras) {
        var url = this.model.baseUrl + 'get/' + pk + '/';
        // extras are optional
        if (extras) {
            url += '?' + this._parseGetParams(extras);
        }
        return this._fetch('GET', url);
    },
    filter: function(filter, exclude) {
        var url = this.model.baseUrl + 'filter/';
        var filterArgs = this._parseGetParams(filter);
        if (filterArgs !== '') {
            url += '?' + filterArgs;
        }
        // exclude is optional
        if (exclude) {
            if (filterArgs === '') {
                url += '?';
            }   else {
                url += '&';
            }
            url += this._parseGetParams(exclude);
        }
        return this._fetch('GET', url);
    },
    form_dict: function(filter) {
        var url = this.model.baseUrl + 'form-info/';
        if (filter) {
            if (typeof filter !== 'object') {
                filter = {'pk': filter};
            }
            var filterArgs = this._parseGetParams(filter);
            if (filterArgs !== '') {
                url += '?' + filterArgs;
            }
        }
        return this._fetch('GET', url);
    },
    create: function(formdata, extras) {
        var url = this.model.baseUrl + 'create/';
        // extras are optional
        if (extras) {
            url += '?' + this._parseGetParams(extras);
        }
        return this._fetch('POST', url, formdata);
    },
    update: function(filter, formdata, extras) {
        if (typeof filter !== 'object') {
            filter = {'pk': filter};
        }
        var url = this.model.baseUrl + 'update/';
        var filterArgs = this._parseGetParams(filter);
        if (filterArgs !== '') {
            url += '?' + filterArgs;
        }
        // extras are optional
        if (extras) {
            if (filterArgs === '') {
                url += '?';
            }   else {
                url += '&';
            }
            url += this._parseGetParams(extras);
        }
        return this._fetch('POST', url, formdata);
    },
    delete: function(filter, extras) {
        if (typeof filter !== 'object') {
            filter = {'pk': filter};
        }
        var url = this.model.baseUrl + 'delete/';
        var filterArgs = this._parseGetParams(filter);
        if (filterArgs !== '') {
            url += '?' + filterArgs;
        }
        // extras are optional
        if (extras) {
            if (filterArgs === '') {
                url += '?';
            }   else {
                url += '&';
            }
            url += this._parseGetParams(extras);
        }
        return this._fetch('POST', url);
    },
    _fetch: function(method, url, data) {
        var _this = this;

        // Check if the user is logged in before sending any request.
        // if (!localStorage.getItem('auth_token')) {
        //     return Promise.reject(new Error('User is not logged in.'));
        //   }

        /**
         * @type {RequestInit}
         */
        var requestOptions = {
            method: method,
            // signal: abortController.signal,
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            },
        };

        if (method === 'POST') {
            /**
             * CSRF token required for POST requests.
             * @type {string}
             */
            requestOptions.headers['X-CSRFToken'] = _this._getCSRFToken();
        }

        for (var key in defaults.headers) {
            var value = defaults.headers[key];
            if (typeof value === 'function') {
                requestOptions.headers[key] = value();
            } else if (value) {
                requestOptions.headers[key] = value;
            }
        }

        if (data && !(data instanceof FormData)) {

            /**
             * If the data is not a FormData object, convert it to FormData.
             * @type {FormData}
             */
            var formData = new FormData();
            for (var key in data) {
                formData.append(key, data[key]);
            }
            requestOptions.body = formData;
        } else {
            requestOptions.body = data;
        }

        /**
         * @type {Promise<any>}
         */
        var promise = new Promise(function(resolve, reject) {
            var baseUrl = defaults.baseUrl || '';
            var requestUrl = baseUrl + url;

            fetch(requestUrl, requestOptions)
                .then(async function(response) {
                    if (response.ok) {
                        return response.json();
                    } else {
                        /**
                         * Adding the keys to try to mimic the old XMLHttpRequest
                         * return value to make sure the functionality doesn't change. We should be refactor this in the future.
                        */
                        const errorData = response;

                        try {
                            const responseText = await response.json();
                            errorData.response = JSON.stringify(responseText);
                            errorData.responseText = JSON.stringify(responseText);
                            throw errorData;
                        } catch (e) {
                            throw response
                        }

                    }
                })
                .then(function(data) {
                    for (let interceptor of defaults.interceptors) {
                        interceptor(data, { status: 200 });
                    }
                    _this.model.callback(resolve)(data.data);
                })
                .catch(function(error) {
                    for (let interceptor of defaults.interceptors) {
                        try {
                            interceptor(error, { status: error.status });
                        } catch (err) {
                            interceptor(error, { status: error.status });
                        }
                    }
                    reject(error);
                });
        });

        return promise;
    },
    _parseGetParams: function(extras) {
        var exs = [];
        for (var key in extras) {
            if(extras.hasOwnProperty(key)) {
                exs.push(key + '=' + encodeURIComponent(extras[key]));
            }
        }
        return exs.join('&');
    },

    _getCSRFToken: function() {
        var pattern = new RegExp('(?:(?:^|.*;)\\s*'
            + encodeURIComponent('csrftoken').replace(/[\-\.\+\*]/g, '\\$&')
            + '\\s*\\=\\s*([^;]*).*$)|^.*$');
        var value = decodeURIComponent(
            document.cookie.replace(pattern, '$1'));
        return value || null;
    }

});

// Model Definition
const Model = Stapes.subclass({
    constructor: function(namespace, resource, version = 'v1', middlewares, options) {
        this.baseUrl = `/api-sileo/${version}/${namespace}/${resource}/`;
        this.middlewares = middlewares || [];
        this.options = utils.extend({}, defaultOptions, options);
        this.objects = new ModelManager(this);
    },

    callback: function(callback) {
        var model = this;
        return function(response) {
            if (model.options.applyGlobalMiddlewares) {
                response = utils.flow(response, globalMiddlewares);
            }
            response = utils.flow(response, model.middlewares);
            window.wrapErrors(callback)(response);
        };
    }
});

Model.addGlobalMiddleware = function() {
    Array.prototype.forEach.call(arguments, middleware => {
        if (typeof middleware === 'function') {
            globalMiddlewares.push(middleware);
        }
    });
};

// Error Wrapper
function wrapErrors(fn) {
    if (!fn.__wrapped__) {
        fn.__wrapped__ = function() {
            try {
                return fn.apply(this, arguments);
            } catch (e) {
                window.onerror(e);
                throw e; // re-throw the error
            }
        };
    }
    return fn.__wrapped__;
}
window.wrapErrors = window.wrapErrors || wrapErrors;

// Exporting
export default {
    Model,
    ModelManager,
    defaults
};
